/**
 * MetricBar (docs/04 §3 "Summary strip", punch item 2.1) — the ONE summary
 * strip, and every tile in it is a filter.
 *
 * Two lessons from PropOS v1 are built in rather than left to taste
 * (docs/11 X-6.5):
 *
 *   1. **Tiles are buttons.** v1's `PortfolioStatsBar` got this right: each
 *      tile carried a click and an active highlight, so the number and the rows
 *      it describes were one gesture apart. A count you cannot act on is
 *      decoration, and decoration that looks clickable is worse.
 *   2. **There is exactly one of these.** v1 shipped TWO stat components with
 *      different designs and no shared type. This component is the reason the
 *      design system catalogues a Summary strip at all — a second one is a bug,
 *      not a variant.
 *
 * The counts are the CALLER's business and must come from one server-side query
 * under the reader's own RLS (v1 fanned out one browser query per property).
 * This component renders numbers; it never fetches them.
 */

import { clsx } from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';

/** The semantic tones a tile may carry. `neutral` is the default. */
export type MetricTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info';

const TONE_ACTIVE: Record<MetricTone, string> = {
  neutral: 'border-brand-700 bg-brand-700 text-on-brand',
  ok: 'border-ok-fg bg-ok-bg text-ok-fg',
  warn: 'border-amber-fg bg-amber-bg text-amber-fg',
  bad: 'border-red-fg bg-red-bg text-red-fg',
  info: 'border-info-fg bg-info-bg text-info-fg',
};

/**
 * Inactive tiles are NEUTRAL whatever their tone. The tone says what kind of
 * number this is; the active state says which one you are looking at. Painting
 * every tile its own colour all the time turns the strip into bunting and makes
 * the selected tile hard to find — and it would put five colour pairs through
 * the contrast gate for no gain.
 */
const INACTIVE = 'border-card-line bg-card text-ink2 hover:border-ink2 hover:text-ink';

export interface MetricTile {
  /** Stable id — the value the caller filters by, or a sentinel for "all". */
  readonly id: string;
  readonly label: string;
  readonly count: number;
  readonly tone?: MetricTone | undefined;
  /** Extra context for screen readers, e.g. the warning window in days. */
  readonly hint?: string | undefined;
}

export interface MetricBarProps {
  /** Accessible name for the group, e.g. "Filter by kind". */
  readonly label: string;
  readonly tiles: readonly MetricTile[];
  /** The active tile id, or null when nothing is filtered. */
  readonly activeId: string | null;
  /** Clicking the active tile passes null — a filter is never a dead end. */
  readonly onSelect: (id: string | null) => void;
  /**
   * Collapse control. Omit both to render an uncollapsible strip; pass both to
   * get the toggle, whose state the caller persists (docs/04 §5).
   */
  readonly collapsed?: boolean | undefined;
  readonly onCollapsedChange?: ((collapsed: boolean) => void) | undefined;
}

export function MetricBar({
  label,
  tiles,
  activeId,
  onSelect,
  collapsed,
  onCollapsedChange,
}: MetricBarProps): ReactNode {
  const collapsible = collapsed !== undefined && onCollapsedChange !== undefined;
  const isCollapsed = collapsible && collapsed;
  const total = tiles.reduce((sum, tile) => sum + tile.count, 0);

  return (
    <div className="flex items-start gap-2">
      {isCollapsed ? (
        // Collapsed still carries the total. A strip that hides its numbers
        // entirely makes the reader expand it to find out whether it was worth
        // expanding.
        <p className="min-h-9 grow content-center text-meta text-ink2">
          {`${total.toLocaleString('en-US')} in ${label.toLowerCase()}`}
        </p>
      ) : (
        <div
          role="group"
          aria-label={label}
          // One scrolling row on a phone, wrapping from 640: a strip that wraps
          // to four lines on a 375px screen pushes the list itself off the
          // fold, and one that scrolls on a laptop hides tiles for no reason.
          className={clsx(
            'flex grow gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            'sm:flex-wrap sm:overflow-visible',
          )}
        >
          {tiles.map((tile) => {
            const active = tile.id === activeId;
            return (
              <button
                key={tile.id}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  onSelect(active ? null : tile.id);
                }}
                className={clsx(
                  'inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-ctl border px-2.5',
                  'text-meta transition-colors duration-(--dur-fast)',
                  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700',
                  active ? TONE_ACTIVE[tile.tone ?? 'neutral'] : INACTIVE,
                )}
              >
                {tile.label}
                {/* Tabular figures so the numbers do not jitter as they change. */}
                <span className="font-semibold tabular-nums">
                  {tile.count.toLocaleString('en-US')}
                </span>
                {tile.hint === undefined ? null : <span className="sr-only">{tile.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
      {collapsible ? (
        <button
          type="button"
          aria-expanded={!isCollapsed}
          onClick={() => {
            onCollapsedChange(!isCollapsed);
          }}
          className={clsx(
            'inline-flex min-h-9 shrink-0 items-center gap-1 rounded-ctl px-2 text-meta',
            'text-ink2 hover:bg-surface-hover hover:text-ink',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700',
          )}
        >
          {isCollapsed ? (
            <ChevronDown size={15} aria-hidden />
          ) : (
            <ChevronUp size={15} aria-hidden />
          )}
          {isCollapsed ? 'Show' : 'Hide'}
        </button>
      ) : null}
    </div>
  );
}
